import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import externalCommissionService from '../../services/ExternalCommissionService.js';
import reportService from '../../services/ReportService.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import {
  Users, DollarSign, FileText, Eye, X, TrendingUp, Calendar,
  CreditCard, Download, ChevronDown, ChevronRight, Filter, Truck, MapPin,
} from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import RefreshingBanner from '../dashboard/components/RefreshingBanner.js';
import { dashboardCacheKey, readDashboardCache, writeDashboardCache } from '../../utils/dashboardCache.js';
import {
  COMMISSION_PALETTE,
  COMMISSION_FINANCIAL,
  getCommissionStatusBadge,
  getSummaryCardClasses,
  getTierClasses,
  getFilterActiveClasses,
  COMMISSION_SUMMARY_META_ROW,
} from '../common/commissionDashboardTheme.js';
import {

  effectiveNetCollect,
  effectiveNetPayable,
  effectiveBalanceFreight,
  effectiveAgentCommission,
  effectiveOwnerShare,
  effectiveAgentRemit,
  effectiveOwnerPayable,
  bookingAgentSummary,
} from '../../utils/externalCommissionMetrics.js';

const ExternalCommissionDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const cacheKey = dashboardCacheKey('ext-commission', user);
  const prefsKey = dashboardCacheKey('ext-commission-prefs', user);
  const initialRef = useRef(null);
  if (initialRef.current == null) {
    const cachedRecords = readDashboardCache(cacheKey);
    initialRef.current = {
      cachedRecords,
      cachedPrefs: readDashboardCache(prefsKey) || {},
      hasCache: Array.isArray(cachedRecords) && cachedRecords.length > 0,
    };
  }
  const { cachedRecords, cachedPrefs, hasCache } = initialRef.current;
  const [loading, setLoading] = useState(!hasCache);
  const [refreshing, setRefreshing] = useState(hasCache);
  const [records, setRecords] = useState(hasCache ? cachedRecords : []);
  const [filteredRecords, setFilteredRecords] = useState(hasCache ? cachedRecords : []);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedPanelRow, setExpandedPanelRow] = useState(null);
  const [totals, setTotals] = useState({
    totalNetReceivable: 0,
    totalCollected: 0,
    totalPendingCollect: 0,
    totalNetPayable: 0,
    totalPaid: 0,
    totalPendingPay: 0,
    totalBalanceFreight: 0,
    totalAgentEarned: 0,
    totalAgentPaid: 0,
    totalAgentDue: 0,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState(cachedPrefs.dateFilter || 'all');
  const [customDates, setCustomDates] = useState(cachedPrefs.customDates || { from_date: '', to_date: '' });
  const [dateRange, setDateRange] = useState(null);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const dateDropdownRef = useRef(null);

  const filterOptions = [
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'all', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

  const getStatusText = (settled, total) => {
    if (settled >= total && total > 0) return 'Paid';
    if (settled > 0) return 'Partial';
    return 'Pending';
  };

  const getStatusBadge = getCommissionStatusBadge;

  const calculateTotals = useCallback((data) => {
    const result = data.reduce((acc, rec) => {
      acc.totalNetReceivable += effectiveNetCollect(rec);
      acc.totalCollected += parseFloat(rec.amount_received_from_agent ?? rec.amount_received_from_owner ?? 0);
      acc.totalPendingCollect += parseFloat(rec.remaining_remit_from_agent ?? rec.remaining_collect ?? 0);
      acc.totalNetPayable += effectiveNetPayable(rec);
      acc.totalPaid += parseFloat(rec.amount_paid_to_owner || 0) + parseFloat(rec.amount_paid_to_agent || 0);
      acc.totalPendingPay += parseFloat(rec.remaining_pay_to_owner ?? 0) + parseFloat(rec.remaining_pay_to_agent ?? rec.remaining_payable ?? 0);
      acc.totalBalanceFreight += effectiveBalanceFreight(rec);
      const earned = parseFloat(effectiveAgentCommission(rec) || rec.total_agent_commission || 0);
      const paidToAgent = parseFloat(rec.amount_paid_to_agent || 0);
      const receivedFromOwner = parseFloat(rec.amount_received_from_owner || 0);
      const paidAmt = Math.max(paidToAgent, Math.min(earned, receivedFromOwner));
      acc.totalAgentEarned += earned;
      acc.totalAgentPaid += paidAmt;
      acc.totalAgentDue += Math.max(0, earned - paidAmt);
      return acc;
    }, {
      totalNetReceivable: 0,
      totalCollected: 0,
      totalPendingCollect: 0,
      totalNetPayable: 0,
      totalPaid: 0,
      totalPendingPay: 0,
      totalBalanceFreight: 0,
      totalAgentEarned: 0,
      totalAgentPaid: 0,
      totalAgentDue: 0,
    });
    setTotals(result);
  }, []);

  const fetchAllRecords = useCallback(async () => {
    try {
      if (!hasCache) setLoading(true);
      else setRefreshing(true);
      let allData = [];
      let page = 1;
      const perPage = 500;

      while (true) {
        const res = await externalCommissionService.getAll(page, perPage, { all: 'true' });
        const batch = res.data?.data || res.data || [];
        if (!Array.isArray(batch)) break;
        allData = allData.concat(batch);
        const totalPages = res.data?.pagination?.pages || 1;
        if (page >= totalPages || batch.length === 0) break;
        page += 1;
      }

      setRecords(allData);
      setFilteredRecords(allData);
      calculateTotals(allData);
      writeDashboardCache(cacheKey, allData);
    } catch (error) {
      console.error('Error fetching external commission dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [calculateTotals, cacheKey, hasCache]);

  useEffect(() => { fetchAllRecords(); }, [fetchAllRecords]);

  useEffect(() => {
    writeDashboardCache(prefsKey, { dateFilter, customDates });
  }, [prefsKey, dateFilter, customDates]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) {
        setIsDateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const calculateDateRange = (filter, from, to) => {
    const today = new Date();
    let s, e;
    switch (filter) {
      case 'current_month': s = new Date(today.getFullYear(), today.getMonth(), 1); e = new Date(today.getFullYear(), today.getMonth() + 1, 0); break;
      case 'last_3_months': s = new Date(today.getFullYear(), today.getMonth() - 3, 1); e = today; break;
      case 'last_6_months': s = new Date(today.getFullYear(), today.getMonth() - 6, 1); e = today; break;
      case 'last_year': s = new Date(today.getFullYear() - 1, today.getMonth(), 1); e = today; break;
      case 'custom': if (from && to) { s = new Date(from); e = new Date(to); } break;
      default: return null;
    }
    if (s && e) return { start_date: s.toISOString().split('T')[0], end_date: e.toISOString().split('T')[0], days: Math.ceil((e - s) / 86400000) + 1 };
    return null;
  };

  useEffect(() => {
    let filtered = [...records];
    if (dateFilter !== 'all') {
      const range = calculateDateRange(dateFilter, customDates.from_date, customDates.to_date);
      if (range) {
        setDateRange(range);
        filtered = filtered.filter((r) => {
          const d = new Date(r.created_at).toISOString().split('T')[0];
          return d >= range.start_date && d <= range.end_date;
        });
      }
    } else {
      setDateRange(null);
    }
    setFilteredRecords(filtered);
    calculateTotals(filtered);
    setSelectedItem(null);
    setExpandedPanelRow(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, dateFilter, customDates]);

  const getDateFilterLabel = () => filterOptions.find((o) => o.value === dateFilter)?.label || 'Select Period';
  const handleDateFilterChange = (v) => { setDateFilter(v); if (v !== 'custom') { setIsDateDropdownOpen(false); setCustomDates({ from_date: '', to_date: '' }); } };
  const handleCustomApply = () => { if (customDates.from_date && customDates.to_date) { setDateFilter('custom'); setIsDateDropdownOpen(false); } };
  const clearFilters = () => { setDateFilter('all'); setCustomDates({ from_date: '', to_date: '' }); setDateRange(null); };

  const downloadExcelReport = async () => {
    try {
      setDownloading(true);
      const params = { date_filter: dateFilter };
      if (dateFilter === 'custom' && customDates.from_date && customDates.to_date) {
        params.from_date = customDates.from_date;
        params.to_date = customDates.to_date;
      }
      await reportService.downloadExternalCommissionReport(params);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download report.');
    } finally {
      setDownloading(false);
    }
  };

  const handleTierClick = (type) => {
    setSelectedItem(type);
    setExpandedPanelRow(null);
  };

  const renderInfoPanel = () => {
    if (!selectedItem) {
      return (
        <div className="p-8 text-center">
          <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Selection</h3>
          <p className="text-sm text-gray-600">Click on any tier to view detailed breakdown</p>
        </div>
      );
    }

    const tierConfig = {
      collect: {
        label: 'Collect from Owner',
        palette: 'amber',
        icon: <DollarSign className="w-6 h-6" />,
        totalComm: totals.totalNetReceivable,
        totalPaid: totals.totalCollected,
        totalPending: totals.totalPendingCollect,
      },
      pay: {
        label: 'Pay to Owner',
        palette: 'sky',
        icon: <Users className="w-6 h-6" />,
        totalComm: totals.totalNetPayable,
        totalPaid: totals.totalPaid,
        totalPending: totals.totalPendingPay,
      },
      freight: {
        label: 'Balance Freight',
        palette: 'emerald',
        icon: <Truck className="w-6 h-6" />,
        totalComm: totals.totalBalanceFreight,
        totalPaid: 0,
        totalPending: totals.totalBalanceFreight,
      },
      records: {
        label: 'Total Records',
        palette: 'violet',
        icon: <FileText className="w-6 h-6" />,
        totalComm: filteredRecords.length,
        totalPaid: filteredRecords.filter((r) => r.settlement_status === 'PAID' || r.payment_status === 'PAID').length,
        totalPending: filteredRecords.filter((r) => (r.settlement_status || r.payment_status) !== 'PAID').length,
      },
    };

    const cfg = tierConfig[selectedItem];
    const palette = COMMISSION_PALETTE[cfg.palette];
    const isRecordsTier = selectedItem === 'records';
    const isFreightTier = selectedItem === 'freight';
    const status = isRecordsTier
      ? `${cfg.totalPaid} settled`
      : isFreightTier
        ? `${filteredRecords.length} records`
        : getStatusText(cfg.totalPaid, cfg.totalComm);

    return (
      <div>
        <div className={`p-4 rounded-t-lg ${palette.panelHeader}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`rounded-lg p-2 ${palette.tierIcon}`}>{cfg.icon}</div>
              <div>
                <h3 className="font-semibold text-lg">{cfg.label}</h3>
                <p className="text-sm text-gray-500">{filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <Button variant="ghost" onClick={() => setSelectedItem(null)} className={`rounded-full p-1 ${palette.panelClose}`}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Financial Summary</h4>
            <div className="space-y-2">
              {!isRecordsTier && (
                <>
                  <div className={`rounded-lg p-3 flex items-center justify-between ${COMMISSION_FINANCIAL.total}`}>
                    <div>
                      <span className="text-xs text-gray-500">{isFreightTier ? 'Total Balance Freight' : 'Net Amount'}</span>
                      <p className="text-lg font-bold">{formatCurrency(cfg.totalComm)}</p>
                    </div>
                    <DollarSign className="w-5 h-5 opacity-70" />
                  </div>
                  {!isFreightTier && (
                    <>
                      <div className={`rounded-lg p-3 flex items-center justify-between ${COMMISSION_FINANCIAL.paid}`}>
                        <div>
                          <span className="text-xs text-gray-500">{selectedItem === 'collect' ? 'Collected' : 'Paid'}</span>
                          <p className="text-lg font-bold">{formatCurrency(cfg.totalPaid)}</p>
                        </div>
                        <TrendingUp className="w-5 h-5 opacity-70" />
                      </div>
                      <div className={`rounded-lg p-3 flex items-center justify-between ${COMMISSION_FINANCIAL.pending}`}>
                        <div>
                          <span className="text-xs text-gray-500">{selectedItem === 'collect' ? 'Pending Collect' : 'Pending Pay'}</span>
                          <p className="text-lg font-bold">{formatCurrency(cfg.totalPending)}</p>
                        </div>
                        <CreditCard className="w-5 h-5 opacity-70" />
                      </div>
                    </>
                  )}
                </>
              )}
              {isRecordsTier && (
                <div className={`rounded-lg p-3 flex items-center justify-between ${COMMISSION_FINANCIAL.neutral}`}>
                  <div>
                    <span className="text-xs text-gray-500">Total Records</span>
                    <p className="text-lg font-bold">{cfg.totalComm}</p>
                  </div>
                  <FileText className="w-5 h-5 opacity-70" />
                </div>
              )}
            </div>
          </div>

          {!isRecordsTier && !isFreightTier && (
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <span className="text-sm text-gray-600">Status:</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(status)}`}>{status}</span>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Breakdown by Record</h4>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredRecords.map((rec) => {
                const isExp = expandedPanelRow === rec.external_commission_id;
                let amount = 0;
                let settled = 0;

                if (selectedItem === 'collect') {
                  amount = effectiveNetCollect(rec);
                  settled = parseFloat(rec.amount_received_from_agent ?? rec.amount_received_from_owner ?? 0);
                  if (amount === 0 && settled === 0) return null;
                } else if (selectedItem === 'pay') {
                  amount = effectiveNetPayable(rec);
                  settled = parseFloat(rec.amount_paid_to_owner || 0) + parseFloat(rec.amount_paid_to_agent || 0);
                  if (amount === 0 && settled === 0) return null;
                } else if (selectedItem === 'freight') {
                  amount = effectiveBalanceFreight(rec);
                  if (amount === 0) return null;
                } else {
                  amount = parseFloat(rec.balance_freight || 0);
                }

                const rowStatus = selectedItem === 'records'
                  ? (rec.settlement_status || rec.payment_status || 'PENDING')
                  : selectedItem === 'freight'
                    ? formatCurrency(amount)
                    : getStatusText(settled, amount);

                return (
                  <div key={rec.external_commission_id} className="bg-gray-50 rounded-lg overflow-hidden">
                    <div
                      className="p-3 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => setExpandedPanelRow(isExp ? null : rec.external_commission_id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {isExp ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />}
                          <span className="text-xs font-semibold text-gray-700">{rec.external_commission_id}</span>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusBadge(rowStatus)}`}>{rowStatus}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs ml-5">
                        <span className="text-gray-500">{rec.ml_sheet_id} · {rec.vehicle_owner_name}</span>
                        <div className="flex gap-3">
                          {selectedItem !== 'records' && (
                            <>
                              <span className={`font-semibold ${palette.accent}`}>{formatCurrency(amount)}</span>
                              {!isFreightTier && <span className="font-semibold text-emerald-700">{formatCurrency(settled)}</span>}
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {isExp && (
                      <div className="px-3 pb-3 pt-1 border-t border-gray-200">
                        <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                          <div className="bg-white rounded p-2"><p className="text-gray-500">Agent Commission</p><p className="font-bold text-violet-700">{formatCurrency(effectiveAgentCommission(rec))}</p></div>
                          <div className="bg-white rounded p-2"><p className="text-gray-500">Owner Share</p><p className="font-bold text-emerald-700">{formatCurrency(effectiveOwnerShare(rec))}</p></div>
                          <div className="bg-white rounded p-2"><p className="text-gray-500">To Pay Freight</p><p className="font-bold">{formatCurrency(rec.balance_freight)}</p></div>
                          <div className="bg-white rounded p-2"><p className="text-gray-500">Booking Agent</p><p className="font-bold">{bookingAgentSummary(rec)}</p></div>
                          <div className="bg-white rounded p-2"><p className="text-gray-500">Agent Remit</p><p className="font-bold text-amber-700">{formatCurrency(effectiveAgentRemit(rec))}</p></div>
                          <div className="bg-white rounded p-2"><p className="text-gray-500">Pay to Owner</p><p className="font-bold text-sky-700">{formatCurrency(effectiveOwnerPayable(rec))}</p></div>
                          <div className="bg-white rounded p-2"><p className="text-gray-500">Direction</p><p className="font-bold">{rec.settlement_direction || '—'}</p></div>
                          <div className="bg-white rounded p-2"><p className="text-gray-500">Status</p><p className="font-bold">{rec.settlement_status || rec.payment_status}</p></div>
                        </div>
                        <Button
                          variant="link"
                          icon={Eye}
                          onClick={() => navigate(`/external-commissions/view/${rec.external_commission_id}`)}
                          className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-sky-700 hover:text-sky-800 py-1.5 bg-sky-50 border border-sky-100 rounded font-medium"
                        >
                          View Full Details
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading commission data..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto">
        <div className="mb-3 md:mb-6">
          <div className="flex items-center justify-end md:justify-between gap-2 mb-3 md:mb-4">
            <div className="hidden md:block min-w-0">
              <h1 className="text-xl font-bold text-gray-900">{t('pages.commission.dashboard')}</h1>
              <p className="text-sm text-gray-600 mt-1">Click on any tier to view detailed breakdown</p>
            </div>
            <div className="flex flex-row flex-wrap items-center justify-end gap-1.5 md:gap-2 w-full md:w-auto shrink-0">
              <Button mobileIconOnly variant="primary" icon={MapPin} onClick={() => navigate('/external-commissions/hub-insights')}>
                Hub Insights
              </Button>
              <Button mobileIconOnly variant="secondary" icon={Filter} onClick={() => setShowFilters(!showFilters)} mobileLabel={showFilters ? 'Hide Filters' : 'Show Filters'}>
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>
          </div>
          <RefreshingBanner show={refreshing} />

          {showFilters && (
            <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 md:p-6 mb-3 md:mb-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
                <div>
                  <label className="hidden md:block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <div className="relative" ref={dateDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                      className="w-full flex items-center justify-between gap-2 px-3 md:px-4 py-2 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 text-sm"
                    >
                      <div className="flex items-center gap-2 min-w-0"><Calendar className="w-4 h-4 shrink-0" /><span className="truncate">{getDateFilterLabel()}</span></div>
                      <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isDateDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isDateDropdownOpen && (
                      <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-sm shadow-lg z-50">
                        <div className="p-4">
                          <div className="space-y-1 mb-4">
                            {filterOptions.map((o) => (
                              <button
                                key={o.value}
                                type="button"
                                onClick={() => handleDateFilterChange(o.value)}
                                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${dateFilter === o.value ? `${getFilterActiveClasses('sky')} font-medium` : 'hover:bg-gray-50 text-gray-700'}`}
                              >
                                {o.label}
                              </button>
                            ))}
                          </div>
                          {dateFilter === 'custom' && (
                            <div className="border-t border-gray-200 pt-4">
                              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
                              <input type="date" value={customDates.from_date} onChange={(e) => setCustomDates({ ...customDates, from_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-3" />
                              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
                              <input type="date" value={customDates.to_date} onChange={(e) => setCustomDates({ ...customDates, to_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-3" />
                              <Button variant="primary" onClick={handleCustomApply} disabled={!customDates.from_date || !customDates.to_date} fullWidth>Apply</Button>
                            </div>
                          )}
                          {dateRange && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <p className="text-xs text-gray-600">
                                <span className="font-medium">Period:</span>{' '}
                                {new Date(dateRange.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} -{' '}
                                {new Date(dateRange.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}{' '}
                                <span className="text-gray-500">({dateRange.days} days)</span>
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="hidden md:block text-sm font-medium text-gray-700 mb-2">{t('common.actions')}</label>
                  <div className="flex flex-row flex-wrap items-center justify-end gap-1.5 md:gap-2 md:justify-start">
                    <Button mobileIconOnly variant="success" icon={Download} onClick={downloadExcelReport} disabled={downloading || filteredRecords.length === 0} mobileLabel={downloading ? 'Downloading...' : 'Download Report'}>
                      {downloading ? 'Downloading...' : 'Download Report'}
                    </Button>
                    <Button mobileIconOnly variant="pagination" onClick={clearFilters}>Clear Filters</Button>
                  </div>
                </div>
              </div>
              {dateFilter !== 'all' && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs border ${COMMISSION_PALETTE.sky.filterBadge}`}>{getDateFilterLabel()}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2 md:gap-4 xl:gap-6 mb-4 md:mb-8">
          <button
            type="button"
            onClick={() => navigate('/external-commission-breakdown')}
            className={`${getSummaryCardClasses('amber')} p-3 md:p-6 text-left w-full cursor-pointer hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between mb-1 md:mb-2 gap-1">
              <h3 className="text-[10px] md:text-sm font-medium text-gray-600 leading-tight">Agent Commission</h3>
              <div className={`rounded-lg p-1.5 md:p-2 shrink-0 hidden sm:flex ${COMMISSION_PALETTE.amber.summaryIcon}`}><DollarSign className="w-4 h-4 md:w-5 md:h-5" /></div>
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 break-all">{formatCurrency(totals.totalAgentEarned)}</p>
            <div className={`mt-1 md:mt-2 pt-1 md:pt-2 border-t ${COMMISSION_PALETTE.amber.summaryMeta}`}>
              <div className={COMMISSION_SUMMARY_META_ROW}>
                <span className="truncate">Paid: {formatCurrency(totals.totalAgentPaid)}</span>
                <span className="truncate">Due: {formatCurrency(totals.totalAgentDue)}</span>
              </div>
            </div>
          </button>
          <div className={`${getSummaryCardClasses('sky')} p-3 md:p-6`}>
            <div className="flex items-center justify-between mb-1 md:mb-2 gap-1">
              <h3 className="text-[10px] md:text-sm font-medium text-gray-600 leading-tight">Pay to Owner</h3>
              <div className={`rounded-lg p-1.5 md:p-2 shrink-0 hidden sm:flex ${COMMISSION_PALETTE.sky.summaryIcon}`}><Users className="w-4 h-4 md:w-5 md:h-5" /></div>
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 break-all">{formatCurrency(totals.totalNetPayable)}</p>
            <div className={`mt-1 md:mt-2 pt-1 md:pt-2 border-t ${COMMISSION_PALETTE.sky.summaryMeta}`}>
              <div className={COMMISSION_SUMMARY_META_ROW}>
                <span className="truncate">Paid: {formatCurrency(totals.totalPaid)}</span>
                <span className="truncate">Pending: {formatCurrency(totals.totalPendingPay)}</span>
              </div>
            </div>
          </div>
          <div className={`${getSummaryCardClasses('emerald')} p-3 md:p-6`}>
            <div className="flex items-center justify-between mb-1 md:mb-2 gap-1">
              <h3 className="text-[10px] md:text-sm font-medium text-gray-600 leading-tight">Balance Freight</h3>
              <div className={`rounded-lg p-1.5 md:p-2 shrink-0 hidden sm:flex ${COMMISSION_PALETTE.emerald.summaryIcon}`}><Truck className="w-4 h-4 md:w-5 md:h-5" /></div>
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 break-all">{formatCurrency(totals.totalBalanceFreight)}</p>
            <div className={`mt-1 md:mt-2 pt-1 md:pt-2 border-t ${COMMISSION_PALETTE.emerald.summaryMeta}`}>
              <div className="flex justify-between text-[10px] md:text-xs"><span>Total at destination</span><span>Across all records</span></div>
            </div>
          </div>
          <div className={`${getSummaryCardClasses('violet')} p-3 md:p-6`}>
            <div className="flex items-center justify-between mb-1 md:mb-2 gap-1">
              <h3 className="text-[10px] md:text-sm font-medium text-gray-600 leading-tight">Total Records</h3>
              <div className={`rounded-lg p-1.5 md:p-2 shrink-0 hidden sm:flex ${COMMISSION_PALETTE.violet.summaryIcon}`}><FileText className="w-4 h-4 md:w-5 md:h-5" /></div>
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900">{filteredRecords.length}</p>
            <div className={`mt-1 md:mt-2 pt-1 md:pt-2 border-t ${COMMISSION_PALETTE.violet.summaryMeta}`}>
              <div className="flex justify-between text-[10px] md:text-xs"><span>Filtered</span><span>Owner commissions</span></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 md:gap-6">
          <div className="xl:col-span-2 bg-white rounded-xl shadow-sm p-3 md:p-6">
            <h2 className="hidden md:block text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Settlement Distribution Overview</h2>
            <div className="space-y-3 md:space-y-4">
              <div
                onClick={() => handleTierClick('collect')}
                className={`cursor-pointer rounded-lg p-3 md:p-4 ${getTierClasses('amber', selectedItem === 'collect')}`}
              >
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className={`rounded-full p-1.5 md:p-2 shrink-0 ${COMMISSION_PALETTE.amber.tierIcon}`}><DollarSign className="w-5 h-5 md:w-6 md:h-6" /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm md:text-lg text-gray-900">Collect from Owner</h3>
                    <p className="text-[10px] md:text-xs text-gray-500 hidden sm:block">Net receivable after owner commission deduction</p>
                  </div>
                </div>
                <div className={`mt-2 md:mt-3 pt-2 md:pt-3 border-t ${COMMISSION_PALETTE.amber.tierDivider}`}>
                  <div className="grid grid-cols-3 gap-1 md:gap-2 text-[10px] md:text-sm">
                    <div><span className="text-gray-500">Net:</span><p className="font-semibold text-gray-900 break-all">{formatCurrency(totals.totalNetReceivable)}</p></div>
                    <div><span className="text-gray-500">Collected:</span><p className="font-semibold text-emerald-700 break-all">{formatCurrency(totals.totalCollected)}</p></div>
                    <div><span className="text-gray-500">Pending:</span><p className="font-semibold text-rose-700 break-all">{formatCurrency(totals.totalPendingCollect)}</p></div>
                  </div>
                </div>
              </div>

              <div
                onClick={() => handleTierClick('pay')}
                className={`cursor-pointer rounded-lg p-3 md:p-4 ${getTierClasses('sky', selectedItem === 'pay')}`}
              >
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className={`rounded-full p-1.5 md:p-2 shrink-0 ${COMMISSION_PALETTE.sky.tierIcon}`}><Users className="w-5 h-5 md:w-6 md:h-6" /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm md:text-lg text-gray-900">Pay to Owner</h3>
                    <p className="text-[10px] md:text-xs text-gray-500 hidden sm:block">Net payable when commission exceeds recovery</p>
                  </div>
                </div>
                <div className={`mt-2 md:mt-3 pt-2 md:pt-3 border-t ${COMMISSION_PALETTE.sky.tierDivider}`}>
                  <div className="grid grid-cols-3 gap-1 md:gap-2 text-[10px] md:text-sm">
                    <div><span className="text-gray-500">Net:</span><p className="font-semibold text-gray-900 break-all">{formatCurrency(totals.totalNetPayable)}</p></div>
                    <div><span className="text-gray-500">Paid:</span><p className="font-semibold text-emerald-700 break-all">{formatCurrency(totals.totalPaid)}</p></div>
                    <div><span className="text-gray-500">Pending:</span><p className="font-semibold text-rose-700 break-all">{formatCurrency(totals.totalPendingPay)}</p></div>
                  </div>
                </div>
              </div>

              <div
                onClick={() => handleTierClick('freight')}
                className={`cursor-pointer rounded-lg p-3 md:p-4 ${getTierClasses('emerald', selectedItem === 'freight')}`}
              >
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className={`rounded-full p-1.5 md:p-2 shrink-0 ${COMMISSION_PALETTE.emerald.tierIcon}`}><Truck className="w-5 h-5 md:w-6 md:h-6" /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm md:text-lg text-gray-900">Balance Freight</h3>
                    <p className="text-[10px] md:text-xs text-gray-500 hidden sm:block">Total balance due at destination</p>
                  </div>
                </div>
                <div className={`mt-2 md:mt-3 pt-2 md:pt-3 border-t ${COMMISSION_PALETTE.emerald.tierDivider}`}>
                  <div className="grid grid-cols-3 gap-1 md:gap-2 text-[10px] md:text-sm">
                    <div><span className="text-gray-500">Total:</span><p className="font-semibold text-gray-900 break-all">{formatCurrency(totals.totalBalanceFreight)}</p></div>
                    <div><span className="text-gray-500">Records:</span><p className="font-semibold text-gray-900">{filteredRecords.length}</p></div>
                    <div><span className="text-gray-500">Avg:</span><p className="font-semibold text-gray-900 break-all">{formatCurrency(filteredRecords.length ? totals.totalBalanceFreight / filteredRecords.length : 0)}</p></div>
                  </div>
                </div>
              </div>

              <div
                onClick={() => handleTierClick('records')}
                className={`cursor-pointer rounded-lg p-3 md:p-4 ${getTierClasses('violet', selectedItem === 'records')}`}
              >
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className={`rounded-full p-1.5 md:p-2 shrink-0 ${COMMISSION_PALETTE.violet.tierIcon}`}><FileText className="w-5 h-5 md:w-6 md:h-6" /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm md:text-lg text-gray-900">Total Records</h3>
                    <p className="text-[10px] md:text-xs text-gray-500 hidden sm:block">All vehicle owner commission records</p>
                  </div>
                </div>
                <div className={`mt-2 md:mt-3 pt-2 md:pt-3 border-t ${COMMISSION_PALETTE.violet.tierDivider}`}>
                  <div className="grid grid-cols-3 gap-1 md:gap-2 text-[10px] md:text-sm">
                    <div><span className="text-gray-500">Count:</span><p className="font-semibold text-gray-900">{filteredRecords.length}</p></div>
                    <div><span className="text-gray-500">Settled:</span><p className="font-semibold text-emerald-700">{filteredRecords.filter((r) => r.settlement_status === 'PAID').length}</p></div>
                    <div><span className="text-gray-500">Pending:</span><p className="font-semibold text-rose-700">{filteredRecords.filter((r) => r.settlement_status !== 'PAID').length}</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="xl:col-span-1">
            <div className="bg-white rounded-xl shadow-sm xl:sticky xl:top-6 overflow-hidden">
              {renderInfoPanel()}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ExternalCommissionDashboard;
